import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import connectToFluxibleContext from 'js/lib/connectToFluxibleContext';

import { Box } from '@coursera/coursera-ui';

import {
  getHighlightSettings,
  updateHighlightSettings,
} from 'bundles/account-settings/actions/HighlightSettingsActions';
import SaveButton from 'bundles/account-settings/components/common/SaveButton';
import StatusMessage from 'bundles/account-settings/components/common/StatusMessage';
import Setting from 'bundles/account-settings/components/sections/highlights/Setting';
import { UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import waitForStores from 'bundles/phoenix/lib/waitForStores';

import _t from 'i18n!nls/account-settings';

import 'css!./__styles__/NotesAndHighlightingSettings';

type OuterProps = {
  userId: number;
};

type InnerProps = {
  disableDataAggregation: boolean;
  disableTopHighlightsDisplay: boolean;
  updateStatus: any;
} & OuterProps;

type State = {
  disableDataAggregation: boolean;
  disableTopHighlightsDisplay: boolean;
};

export class NotesAndHighlightingSettings extends React.Component<InnerProps, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof NotesAndHighlightingSettings.contextTypes>;

  constructor(props: InnerProps) {
    super(props);

    const { disableDataAggregation, disableTopHighlightsDisplay } = props;

    this.state = {
      disableDataAggregation,
      disableTopHighlightsDisplay,
    };
  }

  handleSave = () => {
    const { userId } = this.props;
    const { disableDataAggregation, disableTopHighlightsDisplay } = this.state;
    const { executeAction } = this.context;

    executeAction(updateHighlightSettings, { userId, disableDataAggregation, disableTopHighlightsDisplay });
  };

  handleNoteSettingToggle = (newStatus: 'on' | 'off') => {
    this.setState({ disableDataAggregation: newStatus === 'off' });
  };

  render() {
    const { updateStatus } = this.props;
    const { disableDataAggregation } = this.state;

    return (
      <div className="rc-NotesAndHighlightingSettings">
        <h2 className="display-2-text top-section-title preferences-heading" id="a11y-navigation-heading">
          {_t('Notes & Highlights')}
        </h2>

        <Setting
          ctaTitle={_t('Notes & Highlights')}
          description={_t(
            `Your highlighting and note-taking activity can be used in combination with that of other
            learners in the course to determine the most highlighted phrases.
            No personal information is displayed to other learners.`
          )}
          onText={_t('Allow')}
          offText={_t('Do not allow')}
          onToggle={this.handleNoteSettingToggle}
          enabled={!disableDataAggregation}
        />

        <Box alignItems="center" rootClassName="save-container">
          <SaveButton onClick={this.handleSave} text={_t('Save')} updating={updateStatus === UpdateStates.UPDATING} />
          <StatusMessage status={updateStatus} />
        </Box>
      </div>
    );
  }
}

export default compose<InnerProps, OuterProps>(
  connectToFluxibleContext<InnerProps>(({ executeAction }, { userId }) => {
    executeAction(getHighlightSettings, { userId: String(userId) });
  }),
  waitForStores(['HighlightSettingsStore'], ({ HighlightSettingsStore }, props) => {
    return {
      ...props,
      disableDataAggregation: HighlightSettingsStore.getDisableDataAggregation(),
      disableTopHighlightsDisplay: HighlightSettingsStore.getDisableTopHighlightsDisplay(),
      updateStatus: HighlightSettingsStore.getUpdateStatus(),
    };
  })
)(NotesAndHighlightingSettings);
